import { storeToRefs } from "pinia";
import { computed, type MaybeRefOrGetter, readonly, shallowRef, toValue, watch } from "vue";

import { useEnvironmentStore } from "@/stores/useEnvironmentStore.ts";

import {
    useDistrictTokenApplicationsRepository,
} from "@/composables/api/repositories/pgc/useDistrictTokenApplicationsRepository.ts";

import type { DistrictApplicationDTO } from "@/schemas/pgc-api";
import type {
    PublicDistrictApplicationsOrderByField,
} from "../repositories/pgc/useDistrictsRepository.ts";

import type {
    DistrictApplicationsPublicTableFilters,
    DistrictApplicationsPublicTableOptions,
} from "@/composables/district-applications-list/types";

import type { TableOrdering } from "@/composables/shared/useTableOrdering.ts";
import type { TablePagination } from "@/composables/shared/useTablePagination.ts";

/**
 * Composable used for managing district applications list state
 */
export function usePublicDistrictApplications({ filters, ordering, pagination, districtToken }: {
    filters: MaybeRefOrGetter<DistrictApplicationsPublicTableFilters>;
    ordering: MaybeRefOrGetter<TableOrdering<PublicDistrictApplicationsOrderByField>>;
    pagination: MaybeRefOrGetter<TablePagination>;
    districtToken: MaybeRefOrGetter<string | null>;
}) {
    const { activeEnvironment } = storeToRefs(useEnvironmentStore());
    const { getDistrictApplicationsByDistrictToken } = useDistrictTokenApplicationsRepository();

    const districtApplications = shallowRef<DistrictApplicationDTO[]>([]);
    const isFetching = shallowRef<boolean>(false);
    const totalNoOfRecords = shallowRef<number>(0);

    // Build query parameters based on the provided filters, ordering, and pagination
    const queryParameters = computed<DistrictApplicationsPublicTableOptions>(() => {
        return { ...toValue(filters), ...toValue(ordering), ...toValue(pagination) };
    });

    /**
     * Fetches public district applications
     */
    async function fetchPublicDistrictApplications() {
        const districtTokenValue = toValue(districtToken);
        
        if (!districtTokenValue) {
            console.error("District ID is not set.");
            districtApplications.value = [];
            totalNoOfRecords.value = 0;
            return;
        }

        isFetching.value = true;

        try {
            const response = await getDistrictApplicationsByDistrictToken(districtTokenValue, queryParameters.value);
            districtApplications.value = response.applications ?? [];
            totalNoOfRecords.value = response.metadata?.totalCount ?? 0;
        } catch (e) {
            console.error("Could not fetch public district applications: ", e);
            districtApplications.value = [];
            totalNoOfRecords.value = 0;
        } finally {
            isFetching.value = false;
        }
    }

    // Re-fetch district applications when query parameters, environment or district ID changes
    watch([queryParameters, activeEnvironment, () => toValue(districtToken)], async () => {
        await fetchPublicDistrictApplications();
    }, { deep: true });

    return {
        districtApplications,
        isFetching: readonly(isFetching),
        totalNoOfRecords: readonly(totalNoOfRecords),
        fetchPublicDistrictApplications,
    };
}